import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/cards";
import { Eye, EyeOff } from "lucide-react";

function PasswordChangeForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (newPassword) {
      setPasswordErrors(validatePassword(newPassword));
    }
  }, [newPassword]);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push(`At least ${minLength} characters`);
    if (!hasUpperCase) errors.push('One uppercase letter');
    if (!hasLowerCase) errors.push('One lowercase letter');
    if (!hasNumbers) errors.push('One number');
    if (!hasSpecialChar) errors.push('One special character');

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword && passwordErrors.length > 0) {
      setError('New password does not meet requirements');
      return;
    }

    if (newPassword && newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    const trimmedUsername = newUsername.trim();
    if (trimmedUsername && !/^[a-zA-Z0-9_-]+$/.test(trimmedUsername)) {
      setError('Username can only contain letters, numbers, underscore and hyphen');
      return;
    }

    if (!newPassword && !trimmedUsername) {
      setError('Enter a new password and/or new username');
      return;
    }

    setIsLoading(true);
    try {
      const body = {
        currentPassword,
        ...(newPassword && { newPassword }),
        ...(trimmedUsername && { newUsername: trimmedUsername })
      };
      const response = await fetch(`${API_URL}/api/admin/change-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Profile updated successfully');
        setCurrentPassword('');
        setNewUsername('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 rounded-lg bg-[#0F0F0F] border border-white/10 text-white placeholder:text-[#B8B8B8] focus:outline-none focus:border-[#676DFF]';
  const labelClass = 'block text-sm font-medium text-[#B8B8B8]';

  return (
    <Card className="w-full max-w-md bg-[#151515] border border-white/10">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Lexend, sans-serif' }}>Reset Admin Password &amp; Username</CardTitle>
        <p className="text-sm text-[#B8B8B8] mt-1">Change password and/or username. Leave new password blank to only change username.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className={labelClass}>Current Password</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inputClass} required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B8B8B8] hover:text-white">
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>New Username (optional)</label>
            <input type="text" autoComplete="username" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="e.g. admin" className={inputClass} />
            <p className="text-xs text-[#B8B8B8]">Letters, numbers, underscore and hyphen only.</p>
          </div>
          <div className="space-y-2">
            <label className={labelClass}>New Password (optional)</label>
            <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Leave blank to keep current" className={inputClass} />
            {passwordErrors.length > 0 && (
              <ul className="text-sm text-amber-400 mt-1 list-disc pl-5">
                {passwordErrors.map((error, index) => (<li key={index}>{error}</li>))}
              </ul>
            )}
          </div>
          <div className="space-y-2">
            <label className={labelClass}>Confirm New Password</label>
            <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Only if changing password" className={inputClass} />
          </div>
          {error && <div className="bg-red-500/10 border border-red-500/40 text-red-300 px-4 py-3 rounded-lg text-sm">{error}</div>}
          {success && <div className="bg-green-500/10 border border-green-500/40 text-green-300 px-4 py-3 rounded-lg text-sm">{success}</div>}
          <Button type="submit" className="w-full bg-[#676DFF] hover:bg-[#5A60E8] text-white border-0" disabled={isLoading || (newPassword && passwordErrors.length > 0)}>
            {isLoading ? 'Updating...' : 'Update password / username'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default PasswordChangeForm;